// Comportements communs aux gabarits de la maquette (accueil, produit, entreprise) : apparition
// au défilement (seule ou en cascade), compteurs, FAQ dépliable, zooms en pause hors écran. Doctrine docs/06 : le défilement
// reste natif, rien n'est retenu, et le contenu est lisible sans JS (l'état initial n'existe que
// sous `html.anima-js`).

const COUNTER_DURATION = 1800 // ms, maquette « 30 sept »
const REVEAL_MARGIN = '0px 0px -40px 0px' // une bande de chiffres à peine visible au chargement part quand même
const RADIX = 10
const YEAR_FROM = 1900 // une année (1966) compte à partir de 1900, comme dans la maquette
const YEAR_TO = 2100
const EASE_POWER = 4 // sortie quartique

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const formatNumber = (n) => n.toLocaleString('fr-FR').replace(/ | /g, ' ')

const runCounter = (el) => {
  if (!/^\d+$/.test(el.dataset.animaCount) || reducedMotion()) {
    return
  }
  const to = parseInt(el.dataset.animaCount, RADIX)
  const annee = to > YEAR_FROM && to < YEAR_TO
  const from = annee ? YEAR_FROM : 0
  const start = performance.now()
  const step = (now) => {
    const t = Math.min(1, (now - start) / COUNTER_DURATION)
    const eased = 1 - (1 - t) ** EASE_POWER
    const n = Math.round(from + (to - from) * eased)
    el.textContent = annee ? String(n) : formatNumber(n)
    if (t < 1) {
      requestAnimationFrame(step)
    }
  }
  requestAnimationFrame(step)
}

// Cascade : chaque enfant d'un conteneur `data-anima-stagger` reçoit son rang (--anima-i) ; le
// CSS en tire un délai de 70 ms par rang (components/_base.scss).
const initStagger = () => {
  document.querySelectorAll('[data-anima-stagger]').forEach((list) => {
    [...list.children].forEach((child, i) => {
      child.style.setProperty('--anima-i', String(i))
    })
  })
}

// `is-in` posé une fois à l'entrée dans l'écran : apparitions, cascades, compteurs, et tout
// élément `data-anima-inview` dont le CSS anime l'arrivée (pastilles du calendrier…).
const initReveal = () => {
  const items = [...document.querySelectorAll('[data-anima-reveal], [data-anima-count], [data-anima-stagger], [data-anima-inview]')]
  if (!items.length) {
    return
  }
  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'))
    return
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return
      }
      entry.target.classList.add('is-in')
      if (entry.target.dataset.animaCount) {
        runCounter(entry.target)
      }
      io.unobserve(entry.target)
    })
  }, {
    rootMargin: REVEAL_MARGIN
  })
  items.forEach((el) => io.observe(el))
}

// FAQ : toutes fermées au chargement, une seule ouverte à la fois (maquette « 30 sept »). Sans
// JS les réponses restent dépliées : l'état replié n'existe que sous `html.anima-js`.
const initFaq = () => {
  document.querySelectorAll('[data-anima-faq]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const list = btn.closest('.anima-faq-list')
      const open = btn.getAttribute('aria-expanded') === 'true'
      const buttons = list ? [...list.querySelectorAll('[data-anima-faq]')] : [btn]
      buttons.forEach((b) => {
        const on = b === btn && !open
        b.setAttribute('aria-expanded', on ? 'true' : 'false')
        const panel = document.getElementById(b.getAttribute('aria-controls'))
        if (panel) {
          panel.classList.toggle('is-open', on)
        }
      })
    })
  })
}

// Calendrier des fiches produit : l'indication « Faites défiler » s'efface au premier défilement
// horizontal du tableau.
const initCalendrier = () => {
  document.querySelectorAll('[data-anima-cal]').forEach((scroll) => {
    const hint = scroll.previousElementSibling
    if (hint && hint.classList.contains('anima-cal-hint')) {
      scroll.addEventListener('scroll', () => hint.classList.add('is-seen'), {
        once: true,
        passive: true
      })
    }
  })
}

// Zooms lents en boucle (hero, fermes) : en pause quand ils sont hors de l'écran.
const initZooms = () => {
  const zooms = document.querySelectorAll('.anima-hero-zoom, .anima-ehero-zoom, .anima-phero-zoom, .anima-ferme-photo')
  if (!zooms.length || !('IntersectionObserver' in window)) {
    return
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.classList.toggle('anima-hors-ecran', !entry.isIntersecting))
  })
  zooms.forEach((el) => io.observe(el))
}

const init = () => {
  initStagger()
  initReveal()
  initFaq()
  initCalendrier()
  initZooms()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
